/**
 * Gate 2 — vector database connection form.
 *
 * Provider list and normaliser are reused from the Gate 1 readiness form so
 * providers stay defined in one place. User-facing fields are connection
 * identity only. Retrieval parameters (query vector, dimension, top K, vector
 * column, num candidates) are resolved internally by the backend.
 */
import {
  VECTOR_STORE_PROVIDER_OPTIONS,
  isVectorStoreProviderUnavailable,
  normalizeVectorStoreProvider,
  sanitizePortInput,
  vectorStoreProviderMenuLabel,
} from "../../utils/vectorStoreSurfaceForm";

export {
  VECTOR_STORE_PROVIDER_OPTIONS,
  isVectorStoreProviderUnavailable,
  normalizeVectorStoreProvider,
  sanitizePortInput,
  vectorStoreProviderMenuLabel,
};

export const EMPTY_VECTOR_FORM = {
  provider: "",
  host: "",
  port: "5432",
  database: "",
  username: "",
  table: "documents",
  indexName: "",
  namespace: "",
  collection: "",
  vectorPath: "",
  url: "",
};

export const EMPTY_VECTOR_CREDENTIALS = {
  password: "",
  apiKey: "",
  connectionString: "",
};

const CREDENTIAL_PLACEHOLDER = "Not stored — used for this run only";

export const GATE2_VECTOR_FIELDS = {
  pgvector: [
    { key: "host", label: "Host", required: true, placeholder: "127.0.0.1" },
    { key: "port", label: "Port", required: true, placeholder: "5432", kind: "port" },
    { key: "database", label: "Database", required: false, placeholder: "rag_poc" },
    { key: "username", label: "Username", required: true },
    {
      key: "password",
      label: "Password",
      required: true,
      credential: true,
      placeholder: CREDENTIAL_PLACEHOLDER,
    },
    { key: "table", label: "Table", required: true },
  ],
  pinecone: [
    {
      key: "apiKey",
      label: "API key",
      required: true,
      credential: true,
      placeholder: CREDENTIAL_PLACEHOLDER,
    },
    { key: "indexName", label: "Index name", required: true, placeholder: "my-index" },
    { key: "namespace", label: "Namespace", required: false, placeholder: "optional" },
  ],
  mongodb: [
    {
      key: "connectionString",
      label: "Connection string",
      required: true,
      credential: true,
      placeholder: CREDENTIAL_PLACEHOLDER,
      fullWidth: true,
    },
    { key: "database", label: "Database", required: false, placeholder: "rag" },
    { key: "collection", label: "Collection", required: true, placeholder: "documents" },
    { key: "indexName", label: "Index name", required: true, placeholder: "vector_index" },
    { key: "vectorPath", label: "Vector path", required: true, placeholder: "embedding" },
  ],
  weaviate: [
    {
      key: "url",
      label: "URL / Endpoint",
      required: true,
      placeholder: "https://example.weaviate.network",
      fullWidth: true,
    },
    {
      key: "apiKey",
      label: "API key",
      required: true,
      credential: true,
      placeholder: CREDENTIAL_PLACEHOLDER,
    },
    { key: "collection", label: "Collection", required: true, placeholder: "Documents" },
  ],
};

export function gate2FieldsForProvider(provider) {
  return GATE2_VECTOR_FIELDS[normalizeVectorStoreProvider(provider)] || [];
}

/** The one secret each provider needs. Stored on Gate 1 and reused here. */
export function credentialFieldForProvider(provider) {
  const fields = gate2FieldsForProvider(provider);
  return fields.find((field) => field.credential) || null;
}

/**
 * Map the connection saved in Gate 1 — System & Evaluation Readiness onto
 * this form. Non-secret fields carry over. The secret is hydrated separately
 * from the stored Gate 1 config and is not shown on this screen.
 */
export function formFromGate1Config(config = {}) {
  const provider = normalizeVectorStoreProvider(config.provider);
  return {
    ...EMPTY_VECTOR_FORM,
    provider,
    host: config.host != null ? String(config.host) : "",
    port: config.port != null ? String(config.port) : EMPTY_VECTOR_FORM.port,
    database: config.database || "",
    username: config.username || "",
    table: config.table || EMPTY_VECTOR_FORM.table,
    indexName: config.indexName || "",
    namespace: config.namespace || "",
    collection: config.collection || "",
    vectorPath: config.vectorPath || "",
    url: config.url || config.endpoint || "",
  };
}

/**
 * Non-secret fields the user should see before running, as label/value pairs.
 * Used for the read-only summary of a connection carried over from Gate 1.
 */
export function connectionSummaryRows(form) {
  return gate2FieldsForProvider(form?.provider)
    .filter((field) => !field.credential)
    .map((field) => ({ key: field.key, label: field.label, value: form[field.key] }))
    .filter((row) => String(row.value ?? "").trim());
}

/** Digits only — used for port-adjacent numeric fields if needed. */
export function sanitizeDigits(raw) {
  return String(raw ?? "").replace(/\D/g, "");
}

/** Every required non-secret field for the chosen provider is filled. Secrets come from Gate 1. */
export function vectorFormComplete(form) {
  const provider = normalizeVectorStoreProvider(form?.provider);
  if (!provider || isVectorStoreProviderUnavailable(provider)) return false;
  return gate2FieldsForProvider(provider).every((field) => {
    if (!field.required || field.credential) return true;
    return Boolean(String(form[field.key] ?? "").trim());
  });
}

/**
 * Request payload for POST /gate2/execution/retrieve-context.
 * Only user-facing connection fields are sent. Retrieval parameters
 * (topK, vectorColumn, numCandidates, query vector, dimension) are
 * resolved internally by the backend.
 */
export function buildRetrievePayload(form, credentials = {}) {
  const provider = normalizeVectorStoreProvider(form?.provider);

  if (provider === "pinecone") {
    return {
      vector_store_config: {
        provider,
        indexName: String(form.indexName || "").trim(),
        ...(String(form.namespace || "").trim()
          ? { namespace: String(form.namespace).trim() }
          : {}),
      },
      credentials: { apiKey: credentials.apiKey || "" },
    };
  }

  if (provider === "mongodb") {
    return {
      vector_store_config: {
        provider,
        database: String(form.database || "").trim(),
        collection: String(form.collection || "").trim(),
        indexName: String(form.indexName || "").trim(),
        vectorPath: String(form.vectorPath || "").trim(),
      },
      credentials: { connectionString: credentials.connectionString || "" },
    };
  }

  if (provider === "weaviate") {
    return {
      vector_store_config: {
        provider,
        url: String(form.url || "").trim(),
        collection: String(form.collection || "").trim(),
      },
      credentials: { apiKey: credentials.apiKey || "" },
    };
  }

  return {
    vector_store_config: {
      provider: "pgvector",
      host: String(form.host || "").trim(),
      port: Number(form.port) || 5432,
      database: String(form.database || "").trim(),
      username: String(form.username || "").trim(),
      table: String(form.table || "documents").trim(),
    },
    credentials: { password: credentials.password || "" },
  };
}

/**
 * Request payload for POST /readiness/vector-store-check from the Gate 2 card.
 * Pinecone uses describe_index (no query vector). Other stores reuse the
 * retrieve fields plus a dummy query vector when Gate 1 recorded a dimension.
 */
export function buildVectorProbeConfig(form, credentials = {}, extras = {}) {
  const provider = normalizeVectorStoreProvider(form?.provider);
  if (!provider) return { error: "Select a vector database before testing." };

  if (provider === "pinecone") {
    const indexName = String(form.indexName || "").trim();
    const apiKey = String(credentials.apiKey || "").trim();
    if (!indexName) return { error: "Enter the Pinecone index name before testing." };
    if (!apiKey) return { error: "Enter the Pinecone API key before testing." };
    const namespace = String(form.namespace || "").trim();
    return {
      vector_store_config: {
        provider,
        operation: "describe_index",
        index_name: indexName,
        ...(namespace ? { namespace } : {}),
        api_key: apiKey,
      },
    };
  }

  const dimension = Number(extras.dimension);
  if (!Number.isInteger(dimension) || dimension <= 0) {
    return {
      error:
        "This store needs the embedding dimension saved on System Readiness to run a live test. Confirm execution to query it with a real prompt.",
    };
  }

  const built = buildRetrievePayload(form, credentials);
  return {
    vector_store_config: {
      ...built.vector_store_config,
      ...built.credentials,
      dimension,
      query_vector: Array(dimension).fill(0),
      top_k: built.vector_store_config.topK || 3,
    },
  };
}
